import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { ContextMenuProps, MenuItem as MenuItemType, Position } from "./types";

const SubMenu = ({
    items,
    parentPosition,
    index,
    onClose,
}: {
    items: MenuItemType[];
    parentPosition: Position;
    index: number;
    onClose: () => void;
}) => {
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const rightSpace = window.innerWidth - (parentPosition.x + rect.width);
            const bottomSpace = window.innerHeight - (parentPosition.y + index * 28 + rect.height);

            setPosition({
                x: rightSpace < rect.width ? -rect.width : rect.width,
                y: bottomSpace < 0 ? -rect.height + 28 : 0,
            });
        }
    }, [parentPosition, index]);

    return (
        <div
            ref={menuRef}
            className="absolute bg-gray-900/70 backdrop-blur-lg rounded-lg shadow-lg py-2 min-w-[200px] border border-white/10"
            style={{
                left: position.x,
                top: position.y,
            }}>
            {items.map((item) => (
                <MenuItemComponent key={item.id} item={item} onClose={onClose} />
            ))}
        </div>
    );
};

const MenuItemComponent = ({ item, onClose }: { item: MenuItemType; onClose: () => void }) => {
    const [showSubmenu, setShowSubmenu] = useState(false);
    const itemRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });

    if (item.separator) {
        return <div className="h-px bg-white/10 my-1 mx-2" />;
    }

    const handleMouseEnter = () => {
        if (item.submenu) {
            setShowSubmenu(true);
            if (itemRef.current) {
                const rect = itemRef.current.getBoundingClientRect();
                setPosition({ x: rect.right, y: rect.top });
            }
        }
    };

    const handleClick = () => {
        if (!item.disabled) {
            item.onClick?.();
            if (!item.submenu) {
                onClose(); // Close the menu if it's not a submenu trigger
            }
        }
    };

    return (
        <div
            ref={itemRef}
            className={`px-4 py-1.5 flex items-center gap-3 text-white/90 hover:bg-white/10 transition-colors duration-150 ${
                item.disabled ? "text-white/30 hover:bg-transparent" : "cursor-pointer"
            }`}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setShowSubmenu(false)}>
            {item.icon && <span className="w-4 h-4 opacity-90">{item.icon}</span>}
            <span className="flex-1">{item.label}</span>
            {item.shortcut && <span className="text-xs text-white/50">{item.shortcut}</span>}
            {item.submenu && <ChevronRight className="w-4 h-4 opacity-50" />}
            {showSubmenu && item.submenu && <SubMenu items={item.submenu} parentPosition={position} index={0} onClose={onClose} />}
        </div>
    );
};

export const ContextMenu = ({ items, position, onClose }: ContextMenuProps) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    // Adjust position if menu would go off screen
    const adjustedPosition = { ...position };
    if (menuRef.current) {
        const rect = menuRef.current.getBoundingClientRect();
        if (position.x + rect.width > window.innerWidth) {
            adjustedPosition.x = window.innerWidth - rect.width;
        }
        if (position.y + rect.height > window.innerHeight) {
            adjustedPosition.y = window.innerHeight - rect.height;
        }
    }

    return (
        <div
            ref={menuRef}
            className="fixed z-[9999] bg-gray-900/50 backdrop-blur-lg rounded-lg shadow-lg py-2 min-w-[200px] border border-white/10"
            style={{
                left: adjustedPosition.x,
                top: adjustedPosition.y,
            }}>
            {items.map((item) => (
                <MenuItemComponent key={item.id} item={item} onClose={onClose} />
            ))}
        </div>
    );
};
